/** @format */

import {useEffect, useState, useCallback} from 'react';

import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ModalSimple} from 'components/ModalSimple';
import Snackbar from 'components/Snackbar';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {
  usePurchaseAiReportsCardMutation,
  useRequestAiReportsBankPaymentMutation,
} from 'graphql/__generated__';

import PurchaseFormContent from './PurchaseFormContent';
import BankSentContent from './BankSentContent';

interface Props {
  open: boolean;
  onClose: () => void;
  unitPrice: number;
}

const PurchaseAiReportsModal = ({open, onClose, unitPrice}: Props) => {
  const {t} = useTranslation();
  const {mixpanel} = useMixpanelContext();
  const [count, setCount] = useState<number>(1);
  const [countInputValue, setCountInputValue] = useState<string>('1');
  const [loadingAction, setLoadingAction] = useState<'card' | 'bank' | null>(null);
  const [sent, setSent] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const [purchaseCard] = usePurchaseAiReportsCardMutation();
  const [requestBank] = useRequestAiReportsBankPaymentMutation();

  useEffect(() => {
    // Reset on every open/close transition: opening should always show a fresh
    // form (CT-2.13: reopen after bank-success goes back to PurchaseFormContent),
    // closing should clear in-flight UI state.
    setCount(1);
    setCountInputValue('1');
    setLoadingAction(null);
    setSent(false);
    if (!open) {
      setSnackbarOpen(false);
      setSnackbarMessage('');
    }
  }, [open]);

  const showError = useCallback(() => {
    setSnackbarMessage(t('Произошла ошибка. Попробуйте ещё раз'));
    setSnackbarOpen(true);
  }, [t]);

  const handleCountChange = useCallback((raw: string) => {
    if (!/^\d*$/.test(raw)) return;
    setCountInputValue(raw);
    if (raw === '') return;
    setCount(Math.max(1, Number(raw)));
  }, []);

  const handleCountBlur = useCallback(() => {
    if (countInputValue === '' || Number(countInputValue) < 1) {
      setCount(1);
      setCountInputValue('1');
    }
  }, [countInputValue]);

  const handleIncrement = useCallback(() => {
    setCount(c => {
      const next = c + 1;
      setCountInputValue(String(next));
      return next;
    });
  }, []);

  const handleDecrement = useCallback(() => {
    setCount(c => {
      const next = Math.max(1, c - 1);
      setCountInputValue(String(next));
      return next;
    });
  }, []);

  const handleCardClick = useCallback(async () => {
    setLoadingAction('card');
    const total = count * unitPrice;
    mixpanel.track('ai_reports_purchase_card_click', {
      count,
      total,
    });

    try {
      const {data} = await purchaseCard({variables: {count}});
      const url = data?.purchaseAiReportsCard;
      if (!url) {
        setLoadingAction(null);
        return;
      }
      window.location.href = url;
    } catch {
      setLoadingAction(null);
      showError();
    }
  }, [count, mixpanel, purchaseCard, unitPrice, showError]);

  const handleBankClick = useCallback(async () => {
    setLoadingAction('bank');
    const total = count * unitPrice;
    mixpanel.track('ai_reports_purchase_bank_click', {
      count,
      total,
    });

    try {
      const {data} = await requestBank({variables: {count}});
      if (data?.requestAiReportsBankPayment === true) {
        mixpanel.track('ai_reports_purchase_bank_sent', {
          count,
          total,
        });
        setSent(true);
        setLoadingAction(null);
        return;
      }
      // FR-9 edge: data === false — controlled failure, treat as error
      setLoadingAction(null);
      showError();
    } catch {
      setLoadingAction(null);
      showError();
    }
  }, [count, mixpanel, requestBank, unitPrice, showError]);

  const handleClose = useCallback(() => {
    if (loadingAction !== null) return;
    onClose();
  }, [loadingAction, onClose]);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  return (
    <>
      <ModalSimple
        isOpen={open}
        onClose={handleClose}
        headerText={t('Пополнить баланс ИИ-исследований')}
        headerIcon={
          <Box component='span' className='material-symbols-outlined'>
            credit_card
          </Box>
        }
        type={sent ? 'good' : 'primary'}
      >
        {sent ? (
          <BankSentContent onClose={onClose} />
        ) : (
          <PurchaseFormContent
            count={count}
            countInputValue={countInputValue}
            unitPrice={unitPrice}
            loadingAction={loadingAction}
            onCountChange={handleCountChange}
            onCountBlur={handleCountBlur}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onCardClick={handleCardClick}
            onBankClick={handleBankClick}
          />
        )}
      </ModalSimple>
      <Snackbar
        isSnackbarOpen={snackbarOpen}
        handleSnackbarClose={handleSnackbarClose}
        message={snackbarMessage}
        vertical='bottom'
        horizontal='left'
      />
    </>
  );
};

export default PurchaseAiReportsModal;
